export interface MeshRef {
  fullPath: string;
  relativePath: string;
  filename: string;
  matched: boolean;
  matchedFile: File | null;
}

/**
 * Parse URDF XML content to extract mesh file references.
 */
export function parseUrdfMeshRefs(xmlString: string): MeshRef[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, "text/xml");
  const meshElements = doc.querySelectorAll("mesh");
  const refs = new Map<string, MeshRef>();

  meshElements.forEach((el) => {
    const filename = el.getAttribute("filename");
    if (!filename) return;
    // Extract relative path after "package://<package_name>/"
    const match = filename.match(/^package:\/\/[^/]+\/(.+)$/);
    const relativePath = match ? match[1] : filename;
    const basename = relativePath.split("/").pop() || relativePath;
    if (!refs.has(filename)) {
      refs.set(filename, {
        fullPath: filename,
        relativePath,
        filename: basename,
        matched: false,
        matchedFile: null,
      });
    }
  });
  return Array.from(refs.values());
}

/**
 * Recursively traverse a dropped directory entry to collect files.
 */
export async function traverseFileTree(
  entry: FileSystemEntry,
  path: string = ""
): Promise<File[]> {
  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry;
    const file = await new Promise<File>((resolve, reject) => {
      fileEntry.file(resolve, reject);
    });
    // Define webkitRelativePath property on the file object to preserve folder structure
    Object.defineProperty(file, "webkitRelativePath", {
      value: path + file.name,
      writable: true,
      enumerable: true,
      configurable: true,
    });
    return [file];
  }
  if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntry;
    const reader = dirEntry.createReader();
    const entries: FileSystemEntry[] = [];
    // readEntries may return results in batches
    let batch: FileSystemEntry[];
    do {
      batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
        reader.readEntries(resolve, reject);
      });
      entries.push(...batch);
    } while (batch.length > 0);

    const allFiles: File[] = [];
    for (const child of entries) {
      const childFiles = await traverseFileTree(
        child,
        path + entry.name + "/"
      );
      allFiles.push(...childFiles);
    }
    return allFiles;
  }
  return [];
}

export const MESH_EXTENSIONS = new Set([
  ".stl",
  ".dae",
  ".obj",
  ".glb",
  ".gltf",
]);

/** Extensions for materials and textures that meshes may reference. */
export const TEXTURE_EXTENSIONS = new Set([
  ".mtl",
  ".png",
  ".jpg",
  ".jpeg",
  ".tga",
  ".bmp",
  ".tiff",
  ".tif",
]);

export function isAssetFile(filename: string): boolean {
  const ext = filename.slice(filename.lastIndexOf(".")).toLowerCase();
  return MESH_EXTENSIONS.has(ext) || TEXTURE_EXTENSIONS.has(ext);
}

/**
 * Parse a mesh/material file's text content and return relative paths
 * of files it depends on (MTL from OBJ, textures from MTL, images from DAE).
 */
export function parseMeshDeps(filename: string, content: string): string[] {
  const deps: string[] = [];
  const ext = filename.slice(filename.lastIndexOf(".")).toLowerCase();

  if (ext === ".obj") {
    // OBJ → mtllib <file>
    for (const m of content.matchAll(/^\s*mtllib\s+(.+)$/gm)) {
      deps.push(m[1].trim());
    }
  } else if (ext === ".mtl") {
    // MTL → map_Kd, map_Ka, map_Ks, map_Ns, map_d, map_bump, bump, disp, decal
    for (const m of content.matchAll(
      /^\s*(?:map_Kd|map_Ka|map_Ks|map_Ns|map_d|map_bump|bump|disp|decal)\s+(.+)$/gm
    )) {
      const p = m[1].trim();
      if (!p.startsWith("http://") && !p.startsWith("https://")) {
        deps.push(p);
      }
    }
  } else if (ext === ".dae") {
    // DAE → <init_from>file</init_from>
    // Only consider values with a file extension (contains a dot).
    // Values without a dot are internal Collada IDs (e.g. "file1",
    // "base_colorpng") referencing <library_images>/<surface> elements.
    for (const m of content.matchAll(/<init_from>\s*([^<]+?)\s*<\/init_from>/g)) {
      const p = m[1].trim();
      const basename = p.split("/").pop() || p;
      if (
        !p.startsWith("http://") &&
        !p.startsWith("https://") &&
        !p.startsWith("package://") &&
        !p.startsWith("file://") &&
        basename.includes(".")
      ) {
        deps.push(p);
      }
    }
  }
  return deps;
}
